import io from "socket.io-client";
import { getSocketUrl, getSocketPath } from "../config";

const sockets = new Map(); // chave -> socket
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 10;
const EVENTS_TO_MONITOR = ["appMessage", "ticket"];

/**
 * Compat: connectToSocket() => usa a chave 'default' (chat)
 * Novo:   connectToSocket('notifications') => cria/retorna socket isolado
 *         connectToSocket({ key: 'notifications' }) também funciona
 */
const connectToSocket = (arg) => {
  const key = typeof arg === "string" ? arg : (arg && arg.key) || "default";

  const existing = sockets.get(key);
  if (existing?.connected) {
    console.log(`[DIAGNÓSTICO] Conexão Socket.io [${key}] já existe e está conectada`);
    return existing;
  }

  const token = localStorage.getItem("token");
  const url = getSocketUrl();
  const path = getSocketPath();

  console.log("[DIAGNÓSTICO] Tentando conectar ao socket:", url, path, "key:", key);

  try {
    // COMPATIBILIDADE LEGADA:
    // Para a chave 'default' (chat), mantenha o comportamento antigo:
    // se já existir, FECHA e recria (para não quebrar seu fluxo atual).
    if (key === "default" && existing) {
      try {
        console.log("[DIAGNÓSTICO] (legado) Fechando conexão socket existente [default]");
        existing.close();
      } catch {}
      sockets.delete(key);
    }

    // Para QUALQUER outra chave (ex.: 'notifications'), NÃO feche as outras conexões.
    // Assim, o popover fica isolado do chat.
    const socket = io(url, {
      path,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectionDelay: 1000,
      timeout: 60000,
      // Estes dois abaixo vêm do seu código original; ok manter.
      pingTimeout: 60000,
      pingInterval: 25000,
      query: {
        token: token ? JSON.parse(token) : "",
      },
      // extraHeaders não tem efeito no browser; manter não quebra, mas é inútil.
      extraHeaders: {
        Authorization: token ? `Bearer ${JSON.parse(token)}` : "",
      },
    });

    socket.on("connect", () => {
      console.log(`[DIAGNÓSTICO] Socket [${key}] conectado:`, socket.id);
      reconnectAttempts = 0;

      // Depuração de listeners
      if (socket.hasListeners) {
        EVENTS_TO_MONITOR.forEach((event) => {
          try {
            console.log(
              `[DIAGNÓSTICO] [${key}] Evento ${event} tem ${socket.listeners(event).length} listeners`
            );
          } catch {}
        });
      }
    });

    socket.on("connect_error", (error) => {
      console.error(`[DIAGNÓSTICO] Erro de conexão socket [${key}]:`, error);
      reconnectAttempts++;
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.error("[DIAGNÓSTICO] Número máximo de tentativas de reconexão alcançado");
        // Tentar renovar o token se estiver expirado
        if (error?.message && error.message.includes("jwt expired")) {
          console.log("[DIAGNÓSTICO] Token expirado, tentando renovar...");
          refreshToken();
        }
      }
    });

    socket.on("disconnect", (reason) => {
      console.log(`[DIAGNÓSTICO] Socket [${key}] desconectado:`, reason);
      if (reason === "io server disconnect") {
        console.log("[DIAGNÓSTICO] Desconexão forçada pelo servidor, tentando reconectar...");
        try { socket.connect(); } catch {}
      }
    });

    socket.io.on("reconnect", (attempt) => {
      console.log(`[DIAGNÓSTICO] Socket [${key}] reconectado após`, attempt, "tentativas");
    });
    socket.io.on("reconnect_attempt", (attempt) => {
      console.log(`[DIAGNÓSTICO] [${key}] Tentativa de reconexão:`, attempt);
    });
    socket.io.on("reconnect_error", (error) => {
      console.error(`[DIAGNÓSTICO] [${key}] Erro na reconexão:`, error);
    });
    socket.io.on("reconnect_failed", () => {
      console.error(
        `[DIAGNÓSTICO] [${key}] Falha na reconexão após`,
        MAX_RECONNECT_ATTEMPTS,
        "tentativas"
      );
    });

    // Monitor para debugar eventos recebidos
    const originalOnEvent = socket.onevent;
    socket.onevent = function (packet) {
      const eventName = packet?.data?.[0];
      const eventData = packet?.data?.[1];
      if (EVENTS_TO_MONITOR.includes(eventName)) {
        try {
          console.log(
            `[DIAGNÓSTICO][${key}] Evento recebido ${eventName}:`,
            JSON.stringify(eventData).substring(0, 100) + "..."
          );
        } catch {}
      }
      return originalOnEvent.call(this, packet);
    };

    sockets.set(key, socket);
    return socket;
  } catch (error) {
    console.error("[DIAGNÓSTICO] Erro ao inicializar socket:", error);
    return null;
  }
};

// Função para renovar o token e aplicar em TODAS as conexões vivas
const refreshToken = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/refresh_token`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });

    if (response.ok) {
      const data = await response.json();
      if (data && data.token) {
        localStorage.setItem("token", JSON.stringify(data.token));
        console.log("[DIAGNÓSTICO] Token renovado com sucesso, atualizando sockets...");

        // Atualiza o token nas conexões existentes e força reconexão
        for (const [key, s] of sockets.entries()) {
          try {
            // Atualiza query
            s.io.opts.query = s.io.opts.query || {};
            s.io.opts.query.token = data.token;
            // Reconecta
            if (!s.connected) s.connect();
          } catch (e) {
            console.warn(`[DIAGNÓSTICO] Falha ao atualizar token no socket [${key}]`, e);
          }
        }
      }
    } else {
      console.error("[DIAGNÓSTICO] Falha ao renovar token:", response.status);
    }
  } catch (error) {
    console.error("[DIAGNÓSTICO] Erro ao renovar token:", error);
  }
};

// (Opcional) encerrar uma conexão específica (ex.: no logout)
export const closeSocket = (key = "default") => {
  const s = sockets.get(key);
  if (!s) return;
  try {
    s.removeAllListeners();
    s.disconnect();
  } catch {}
  sockets.delete(key);
};

export default connectToSocket;
